import { useState, useEffect, useMemo } from "react";
import { Popover } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { FilePenLine } from "lucide-react";

import { showToast } from "utils/utility";
import { useFlightData } from "libs/data-access";
import { EButton, ETooltip, ESegmentedControl, ENumberInput, ETextArea, EPlaceholder } from "libs/ui";
import { IFlightOnlineBookingList } from "libs/data-access/types";

interface EditRefundChargesProps {
  flightTicket: IFlightOnlineBookingList;
  handleRefresh: () => void;
};

export default function EditFlightRefundCharges({ flightTicket, handleRefresh }: EditRefundChargesProps) {

  const { postFlightData } = useFlightData();

  const [opened, { open, close }] = useDisclosure(false);

  const [isLoading, setIsLoading] = useState(false);
  const [journeySegments, setJourneySegments] = useState<{
    label: string; value: string; disabled: boolean;
  }[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<string>("0");
  const [refundCharges, setRefundCharges] = useState<{
    supplierCharge: number;
    cancellationCharge: number;
    selectedBooking: { id: number; key: string; };
    remark: string | null;
  }[]>([]);


  // Initialize refund charges from props
  useEffect(() => {
    if (!flightTicket?.bookingResponses?.length) return;

    createJourneySegments();
    manageRefundChargeOnLoad();
  }, [flightTicket]);

  let selectedRefundCharge = useMemo(() => {
    return refundCharges.find(
      each =>
        each.selectedBooking.id === Number(selectedSegment.split("/")[0])
        &&
        each.selectedBooking.key === selectedSegment.split("/")[1]
    );
  }, [selectedSegment]);


  // HANDLER FUNCTIONS
  const handleChargeChange = (
    field: 'supplierCharge' | 'cancellationCharge' | 'remark',
    value: number | string
  ) => {
    const updatedCharges = refundCharges.map(charge => {
      if (
        charge.selectedBooking.id === Number(selectedSegment.split("/")[0])
        &&
        charge.selectedBooking.key === selectedSegment.split("/")[1]
      ) {
        return {
          ...charge,
          [field]: value
        };
      };
      return charge;
    });

    setRefundCharges(updatedCharges);
  };

  const createJourneySegments = () => {
    let segments: { label: string; value: string; disabled: boolean; }[] = [];

    flightTicket.bookingResponses.forEach(
      booking => {
        // for galileo journeys
        booking.galileoBookingDetailResponses?.forEach(
          galJourney => segments.push({
            label: `${galJourney.origin} - ${galJourney.destination}`,
            value: `${booking.id}/${galJourney.origin}_${galJourney.destination}`,
            disabled: galJourney.bookingStatus !== "TO_BE_REFUNDED"
          })
        );

        // for navitaire
        booking.navitaireBookingDetailResponses?.forEach(
          navJourney => navJourney.bookingStatus === "TO_BE_REFUNDED" && segments.push({
            label: `${navJourney.origin} - ${navJourney.destination}`,
            value: `${booking.id}/${navJourney.origin}_${navJourney.destination}`,
            disabled: navJourney.bookingStatus !== "TO_BE_REFUNDED"
          })
        );

        // for akbar
        booking.akbarBookingDetailResponses?.forEach(
          abkarJourney => segments.push({
            label: `${abkarJourney.origin} - ${abkarJourney.destination}`,
            value: `${booking.id}/${abkarJourney.origin}_${abkarJourney.destination}`,
            disabled: abkarJourney.bookingStatus !== "TO_BE_REFUNDED"
          })
        );
      }
    );

    // console.log("segments: ", segments);

    setJourneySegments(segments);
  };

  const manageRefundChargeOnLoad = () => {
    let refundChargs: {
      supplierCharge: number;
      cancellationCharge: number;
      selectedBooking: { id: number; key: string; };
      remark: string;
    }[] = [];

    flightTicket.bookingResponses?.forEach(
      booking => {
        // for galileo
        booking.galileoBookingDetailResponses?.forEach(
          journey => refundChargs.push({
            supplierCharge: journey.cancellationChargeResponse?.supplierCharge || 0,
            cancellationCharge: journey.cancellationChargeResponse?.cancellationCharge || 0,
            selectedBooking: { id: booking.id, key: `${journey.origin}_${journey.destination}` },
            remark: ""
          })
        );

        booking.navitaireBookingDetailResponses?.forEach(
          journey => refundChargs.push({
            supplierCharge: journey.cancellationChargeResponse?.supplierCharge || 0,
            cancellationCharge: journey.cancellationChargeResponse?.cancellationCharge || 0,
            selectedBooking: { id: booking.id, key: `${journey.origin}_${journey.destination}` },
            remark: ""
          })
        );
      });

    // console.log("refundChargs: ", refundChargs);

    setRefundCharges(refundChargs);
  };


  // API CALLS
  const handleSaveCharges = async () => {
    if (!flightTicket.uuid) {
      showToast({
        type: "error",
        message: "Booking ID not found!"
      });
      return;
    };

    const selectedCharge = refundCharges.find(
      each =>
        each.selectedBooking.id === Number(selectedSegment.split("/")[0])
        &&
        each.selectedBooking.key === selectedSegment.split("/")[1]
    );
    if (!selectedCharge?.selectedBooking?.id) {
      showToast({
        type: "error",
        message: "Selected segment booking ID not found!"
      });
      return;
    };

    setIsLoading(true);

    try {
      const requestData = {
        bookingId: selectedCharge.selectedBooking.id,
        key: selectedCharge.selectedBooking.key,
        supplierCharge: selectedCharge.supplierCharge,
        cancellationCharge: selectedCharge.cancellationCharge,
        remark: selectedCharge.remark
      };

      const response = await postFlightData(
        `/booking/${requestData.bookingId}/change-cancellation-charges?key=${requestData.key}&supplierCharge=${requestData.supplierCharge}&cancellationCharge=${requestData.cancellationCharge}&remark=${requestData.remark}`,
        null
      );

      if (response.isSuccess) {
        showToast({
          type: "success",
          message: `Refund charges updated successfully for segment ${Number(selectedSegment) + 1}!`
        });
        close();
        handleRefresh();
      } else {
        console.log("Error while updating refund charges", response);
        showToast({
          type: "error",
          message: response.message || "Failed to update refund charges!"
        });
      };
    } catch (error) {
      console.error("Error updating refund charges:", error);
      showToast({
        type: "error",
        message: "An error occurred while updating refund charges."
      });
    } finally {
      setIsLoading(false);
    };
  };


  return (
    <Popover
      position="bottom-end"
      withArrow
      shadow="md"
      opened={opened}
      onChange={open}
    >
      <Popover.Target>
        <div>
          <ETooltip label="Edit Cancellation Charges">
            <div>
              <EButton
                size="sm"
                variant="light"
                color="blue"
                onClick={open}
              >
                <FilePenLine size={20} className="mr-1" />
                <span className="hidden sm:inline">Edit Cancellation Charges</span>
              </EButton>
            </div>
          </ETooltip>
        </div>
      </Popover.Target>

      <Popover.Dropdown>
        <div className="w-96 p-2">
          <h3 className="text-lg font-medium mb-4">Edit Cancellation Charges</h3>

          {/* Segment Selection */}
          <div className="mb-4">
            <ESegmentedControl
              fullWidth
              data={journeySegments}
              value={selectedSegment}
              onChange={setSelectedSegment}
            // data={journeySegments}
            // value={`${rejectTicketState.selectedBooking.id}/${rejectTicketState.selectedBooking.key}`}
            // onChange={(value) => setRejectTicketState({
            //   ...rejectTicketState,
            //   selectedBooking: {
            //     id: Number(value.split("/")[0]),
            //     key: value.split("/")[1]
            //   }
            // })}
            />
            {/* <ESegmentedControl
              value={selectedSegment}
              onChange={setSelectedSegment}
              data={flightTicket.bookingResponses?.map((_, index) => ({
                label: `Journey ${index + 1}`,
                value: String(index)
              })) || []}
            /> */}
          </div>

          {/* Selected Segment Charges */}
          {
            selectedRefundCharge
              ?
              (
                <div className="mb-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Supplier Charge</label>
                      <ENumberInput
                        min={0}
                        // precision={2}
                        className="mt-1"
                        value={selectedRefundCharge.supplierCharge}
                        onChange={(value) => handleChargeChange('supplierCharge', Number(value))}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-600">Cancellation Charge</label>
                      <ENumberInput
                        // precision={2}
                        className="mt-1"
                        value={selectedRefundCharge.cancellationCharge}
                        onChange={(value) => handleChargeChange('cancellationCharge', Number(value))}
                        min={0}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-600">Remark</label>
                      <ETextArea
                        className="mt-1"
                        // value={selectedRefundCharge.remark ?? undefined}
                        onChange={(e) => handleChargeChange('remark', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )
              :
              <EPlaceholder
                title="Please select a journey from above."
              />
          }

          <div className="flex justify-end gap-2 mt-4">
            <EButton
              // size="sm"
              variant="subtle"
              onClick={close}
              disabled={isLoading}
            >
              Cancel
            </EButton>

            <EButton
              // size="sm"
              color="blue"
              onClick={handleSaveCharges}
              loading={isLoading}
            >
              Save Changes
            </EButton>
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
} 